import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NotificationsPanel, type NotificationItem } from '@/components/ds-dlp';
import { notificationsApi } from '../api/client';

interface NotificationsResponse {
  notifications?: NotificationItem[];
  unread_count?: number;
}

/**
 * The top bar's notifications (moved from the Dashboard header, where the
 * bell showed a count and did nothing). Polls the unread list once a minute,
 * so the badge is right on every page. Choosing one marks it read and opens
 * its link when the link is inside the console; "Mark all read" clears the
 * badge.
 */
export function useHeaderNotifications() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.getAll(true).then((r) => r.data as NotificationsResponse),
    refetchInterval: 60_000,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['notifications'] });

  const markRead = useMutation({ mutationFn: (id: number) => notificationsApi.markRead(id), onSettled: refresh });
  const markAll = useMutation({ mutationFn: () => notificationsApi.markAllRead(), onSettled: refresh });

  const items = Array.isArray(data?.notifications) ? data.notifications : [];
  const unreadCount = data?.unread_count ?? items.length;

  const onOpen = (n: NotificationItem) => {
    markRead.mutate(n.id);
    setOpen(false);
    if (n.link && n.link.startsWith('/')) navigate(n.link);
  };

  return {
    count: unreadCount,
    open,
    setOpen,
    panel: (
      <NotificationsPanel
        items={items}
        unreadCount={unreadCount}
        loading={isLoading}
        onOpen={onOpen}
        onMarkAllRead={() => markAll.mutate()}
        markingAll={markAll.isPending}
      />
    ),
  };
}
